<script setup lang="ts">
import dateHelper from "@/core/helpers/dateHelper";
import GiftStatusBadge from "@/birthday-gift/components/GiftStatusBadge.vue";
import {GiftScanStatus, GiftVoucherScan, VoucherStatus} from "@/birthday-gift/interfaces";

defineProps<{
    scans: GiftVoucherScan[];
}>();

const initials = (scan: GiftVoucherScan) =>
    `${scan.customer.first_name.charAt(0)}${scan.customer.last_name.charAt(0)}`.toUpperCase();

/** The scan list has its own vocabulary; map it onto the shared badge. */
const badgeStatus = (status: GiftScanStatus): VoucherStatus => ({
    redeemed: 'redeemed',
    pending: 'sent',
    expired: 'expired',
    failed: 'failed',
} as const)[status];

const scannedLabel = (scan: GiftVoucherScan) => {
    if (!scan.redeemed_at) {
        return scan.sent_at ? `Envoyé le ${dateHelper.shortFormat(scan.sent_at, 'D MMMM')}` : '—';
    }

    return `Remis le ${dateHelper.shortFormat(scan.redeemed_at, 'D MMMM [à] HH:mm')}`;
};
</script>

<template>
  <div class="divide-y divide-gray-light">
    <div
      v-for="scan in scans"
      :key="scan.id"
      class="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3"
    >
      <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lightPurple-lightest text-xs font-semibold text-primary">
        {{ initials(scan) }}
      </span>

      <div class="min-w-[150px] flex-1 basis-40">
        <div class="text-sm font-semibold">
          {{ scan.customer.first_name }} {{ scan.customer.last_name }}
        </div>
        <div class="text-xs text-muted-foreground">
          Anniversaire le {{ dateHelper.shortFormat(scan.birthday_date, 'D MMMM') }} ·
          {{ scan.customer.age }} ans
        </div>
      </div>

      <div class="min-w-[150px] flex-1 basis-48 text-xs text-muted-foreground">
        {{ scannedLabel(scan) }}
        <template v-if="scan.redeemed_by">
          <br>par {{ scan.redeemed_by.name }}
        </template>
      </div>

      <div class="ml-auto flex items-center gap-3">
        <span class="font-mono text-xs text-muted-foreground">{{ scan.code ?? '—' }}</span>
        <GiftStatusBadge :status="badgeStatus(scan.status)" />
      </div>
    </div>

    <div
      v-if="!scans.length"
      class="px-5 py-8 text-center text-sm text-muted-foreground"
    >
      Aucun QR code envoyé pour ce cadeau sur les 30 derniers jours.
    </div>
  </div>
</template>
